import React, { useState, useEffect } from 'react';
import {
    FiShoppingBag, FiSearch, FiEye, FiTruck, FiCheckCircle,
    FiXCircle, FiClock, FiDollarSign, FiUser, FiMapPin, FiPackage,
    FiTrendingUp, FiBox, FiCalendar, FiChevronRight, FiCreditCard
} from 'react-icons/fi';
import { orderService } from '../../../services/api';
import './AdminOrders.css';

const AdminOrders = () => {
    const [orders, setOrders] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showDrawer, setShowDrawer] = useState(false);
    const [statusFilter, setStatusFilter] = useState('all');
    const [actionMessage, setActionMessage] = useState({ text: '', type: '' });
    const [adminNote, setAdminNote] = useState('');

    const fetchOrders = async () => {
        setIsLoading(true);
        try {
            const resp = await orderService.getAllOrders();
            setOrders(resp.data || []);
        } catch (err) {
            console.error('Failed to fetch orders:', err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders();
    }, []);

    const handleUpdateStatus = async (orderId, newStatus) => {
        try {
            await orderService.updateOrderStatus(orderId, newStatus, adminNote);

            setOrders(prev => prev.map(o => o._id === orderId ? { ...o, status: newStatus, remarks: adminNote } : o));
            if (selectedOrder?._id === orderId) {
                setSelectedOrder(prev => ({ ...prev, status: newStatus, remarks: adminNote }));
            }
            setActionMessage({ text: `Order updated to ${newStatus.toUpperCase()}${adminNote ? ' with note' : ''}`, type: 'success' });
            setAdminNote(''); // Clear note after update
            setTimeout(() => setActionMessage({ text: '', type: '' }), 4000);
        } catch (err) {
            console.error('Status update failed:', err);
            let errorMsg = 'Failed to update order status. Please try again.';
            if (err.response && err.response.data) {
                // If the backend sends an error message in a standard field like 'message', 'error', or the response itself is a string
                errorMsg = err.response.data.message || err.response.data.error || JSON.stringify(err.response.data) || errorMsg;
            }
            setActionMessage({ text: errorMsg, type: 'error' });
            setTimeout(() => setActionMessage({ text: '', type: '' }), 5000);
        }
    };

    const filteredOrders = orders.filter(order => {
        const matchesSearch =
            order.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            order.userId?.fullName?.toLowerCase().includes(searchTerm.toLowerCase());
        
        // Hide cancelled orders from the main list as per user requirement, EXCEPT when explicitly in the Cancelled tab
        if (order.status === 'cancelled' && statusFilter !== 'cancelled') return false;

        const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    // Stats calculation
    const stats = {
        total: orders.length,
        completed: orders.filter(o => o.status === 'delivered').length,
        pending: orders.filter(o => o.status === 'pending' || o.status === 'processing').length,
        cancelled: orders.filter(o => o.status === 'cancelled').length
    };

    const getStatusStyle = (status) => {
        const lowerState = (status || '').toLowerCase();
        const styles = {
            pending: { color: '#f59e0b', bg: '#fef3c7', icon: <FiClock /> },
            processing: { color: '#3b82f6', bg: '#dbeafe', icon: <FiPackage /> },
            confirmed: { color: '#2563eb', bg: '#dbeafe', icon: <FiCheckCircle /> },
            shipped: { color: '#8b5cf6', bg: '#ede9fe', icon: <FiTruck /> },
            delivered: { color: '#10b981', bg: '#d1fae5', icon: <FiCheckCircle /> },
            cancelled: { color: '#ef4444', bg: '#fee2e2', icon: <FiXCircle /> }
        };
        return styles[lowerState] || styles.pending;
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="admin-orders-page">
            {actionMessage.text && (
                <div className={`global-action-alert ${actionMessage.type}`}>
                    {actionMessage.type === 'success' ? <FiCheckCircle /> : <FiXCircle />}
                    <span>{actionMessage.text}</span>
                </div>
            )}
            {/* Premium Header Section */}
            <div className="adr-order-banner">
                <div className="adr-banner-content">
                    <span className="adr-tag">Order Hub</span>
                    <h1>Manage Orders</h1>
                    <p>Track and manage all customer orders here.</p>
                </div>
            </div>

            {/* Stats Overview - Premium Match */}
            <div className="order-stats-grid premium">
                {/* Total Orders */}
                <div className="premium-stat-card">
                    <div className="growth-badge positive">+12.5%</div>
                    <div className="premium-icon-box blue">
                        <FiShoppingBag className="p-icon" />
                    </div>
                    <div className="premium-stat-content">
                        <div className="p-label-row">
                            <span className="p-label">Total Orders</span>
                            <div className="p-dot blue"></div>
                        </div>
                        <span className="p-value">{stats.total}</span>
                    </div>
                    <div className="wave-bg"></div>
                </div>

                {/* Completed Orders */}
                <div className="premium-stat-card">
                    <div className="growth-badge positive">+8.4%</div>
                    <div className="premium-icon-box green">
                        <FiCheckCircle className="p-icon" />
                    </div>
                    <div className="premium-stat-content">
                        <div className="p-label-row">
                            <span className="p-label">Completed</span>
                            <div className="p-dot green"></div>
                        </div>
                        <span className="p-value">{stats.completed}</span>
                    </div>
                    <div className="wave-bg"></div>
                </div>

                {/* Pending Orders */}
                <div className="premium-stat-card">
                    <div className="growth-badge positive">+2.1%</div>
                    <div className="premium-icon-box orange">
                        <FiClock className="p-icon" />
                    </div>
                    <div className="premium-stat-content">
                        <div className="p-label-row">
                            <span className="p-label">Pending</span>
                            <div className="p-dot orange"></div>
                        </div>
                        <span className="p-value">{stats.pending}</span>
                    </div>
                    <div className="wave-bg"></div>
                </div>

                {/* Cancelled Orders */}
                <div className="premium-stat-card">
                    <div className="growth-badge negative">-1.5%</div>
                    <div className="premium-icon-box purple">
                        <FiXCircle className="p-icon" />
                    </div>
                    <div className="premium-stat-content">
                        <div className="p-label-row">
                            <span className="p-label">Cancelled</span>
                            <div className="p-dot purple"></div>
                        </div>
                        <span className="p-value">{stats.cancelled}</span>
                    </div>
                    <div className="wave-bg"></div>
                </div>
            </div>

            {/* Controls Row */}
            <div className="order-controls-row">
                <div className="filter-group">
                    {['all', 'pending', 'confirmed', 'delivered', 'cancelled'].map(s => (
                        <button
                            key={s}
                            className={`filter-btn ${statusFilter === s ? 'active' : ''}`}
                            onClick={() => setStatusFilter(s)}
                        >
                            {s === 'all' ? 'All Transactions' : s.charAt(0).toUpperCase() + s.slice(1)}
                        </button>
                    ))}
                </div>
                <div className="order-search-wrap">
                    <FiSearch className="search-icon" />
                    <input
                        type="text"
                        placeholder="Search order # or customer..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {/* Orders Table */}
            <div className="orders-table-container">
                <table className="adr-orders-table">
                    <thead>
                        <tr>
                            <th>ORDER ID</th>
                            <th>CUSTOMER</th>
                            <th>DATE</th>
                            <th>PAYMENT</th>
                            <th>PRICE</th>
                            <th>STATUS</th>
                            <th>ACTION</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            [1, 2, 3, 4, 5].map(i => <tr key={i} className="skeleton-row"><td colSpan="7"></td></tr>)
                        ) : filteredOrders.map(order => (
                            <tr key={order._id}>
                                <td className="order-id-cell" data-label="ORDER ID">
                                    <span className="order-num">{order.orderNumber}</span>
                                    <span className="order-uuid">{order._id.substring(18)}</span>
                                </td>
                                <td data-label="CUSTOMER">
                                    <div className="customer-cell">
                                        <div className="cust-avatar">{order.userId?.fullName?.charAt(0) || 'U'}</div>
                                        <div className="cust-info">
                                            <span className="cust-name">{order.userId?.fullName || 'Guest User'}</span>
                                            <span className="cust-email">{order.userId?.email || 'N/A'}</span>
                                        </div>
                                    </div>
                                </td>
                                <td data-label="DATE">
                                    <span className="date-text">{formatDate(order.createdAt)}</span>
                                </td>
                                <td data-label="PAYMENT">
                                    <div className="payment-badge">
                                        <FiCreditCard /> {order.paymentMethod === 'Cash On Delivery' ? 'COD' : 'Prepaid'}
                                    </div>
                                </td>
                                <td data-label="TOTAL">
                                    <div className="price-tag-wrap">
                                        <span className="pkr-badge">PKR</span>
                                        <span className="order-total">{order.subtotal.toLocaleString()}</span>
                                    </div>
                                </td>
                                <td data-label="STATUS">
                                    <span className="status-pill" style={{
                                        color: getStatusStyle(order.status).color,
                                        backgroundColor: getStatusStyle(order.status).bg
                                    }}>
                                        {getStatusStyle(order.status).icon}
                                        {order.status}
                                    </span>
                                </td>
                                <td data-label="ACTIONS">
                                    <button
                                        className="btn-view-order"
                                        onClick={() => {
                                            setSelectedOrder(order);
                                            setShowDrawer(true);
                                        }}
                                    >
                                        <FiEye /> View
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Order Details Drawer */}
            {showDrawer && selectedOrder && (
                <div className="order-drawer-overlay" onClick={() => setShowDrawer(false)}>
                    <div className="order-drawer" onClick={e => e.stopPropagation()}>
                        <div className="drawer-header">
                            <div className="d-title-area">
                                <h2>Order Details</h2>
                                <span className="d-order-num">{selectedOrder.orderNumber}</span>
                            </div>
                            <button className="close-drawer" onClick={() => setShowDrawer(false)}><FiXCircle /></button>
                        </div>

                        <div className="drawer-body">
                            {/* Summary Section */}
                            <div className="drawer-section status-update">
                                <h3>Order Status</h3>
                                <div className="status-actions">
                                    {['pending', 'confirmed', 'delivered', 'cancelled'].map(s => (
                                        <button
                                            key={s}
                                            className={`status-action-btn ${selectedOrder.status === s ? 'active' : ''}`}
                                            onClick={() => handleUpdateStatus(selectedOrder._id, s)}
                                        >
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="drawer-grid">
                                <div className="drawer-column">
                                    <div className="drawer-section">
                                        <div className="section-header"><FiUser /> Customer Info</div>
                                        <div className="info-card">
                                            <p><strong>Name:</strong> {selectedOrder.userId?.fullName}</p>
                                            <p><strong>Email:</strong> {selectedOrder.userId?.email}</p>
                                            <p><strong>Address:</strong><br /> {selectedOrder.shippingAddress}</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="drawer-column">
                                    <div className="drawer-section">
                                        <div className="section-header"><FiPackage /> Ordered Items</div>
                                        <div className="items-list">
                                            {selectedOrder.items.map((item, idx) => (
                                                <div key={idx} className="order-item-row">
                                                    <img src={item.imageSnapshot || 'https://placehold.co/50x50?text=Item'} alt={item.titleSnapshot} />
                                                    <div className="item-meta">
                                                        <span className="item-title">{item.titleSnapshot}</span>
                                                        <span className="item-sub">PKR {item.priceSnapshot} × {item.qty}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="drawer-footer-summary">
                                <div className="summary-row">
                                    <span>Payment Method:</span>
                                    <span>{selectedOrder.paymentMethod}</span>
                                </div>
                                <div className="summary-row total">
                                    <span>Total Price:</span>
                                    <span>PKR {selectedOrder.subtotal.toLocaleString()}</span>
                                </div>

                                <div className="admin-response-section">
                                    <label>Merchant Response / Note</label>
                                    <textarea 
                                        placeholder="Add a note to show the customer (e.g. 'Shipped via BlueEx', 'Refund initiated')..."
                                        value={adminNote}
                                        onChange={(e) => setAdminNote(e.target.value)}
                                    />
                                </div>

                                {selectedOrder.status === 'pending' ? (
                                    <button
                                        className="btn-drawer-confirm"
                                        onClick={() => handleUpdateStatus(selectedOrder._id, 'confirmed')}
                                    >
                                        <FiCheckCircle /> Confirm Order
                                    </button>
                                ) : (
                                    <div className="status-footer-badge" style={{
                                        color: getStatusStyle(selectedOrder.status).color,
                                        backgroundColor: getStatusStyle(selectedOrder.status).bg,
                                        border: `1px solid ${getStatusStyle(selectedOrder.status).color}44`
                                    }}>
                                        {getStatusStyle(selectedOrder.status).icon}
                                        Order {selectedOrder.status.charAt(0).toUpperCase() + selectedOrder.status.slice(1)}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminOrders;

